// Data-driven SEO landing pages — copy strictly per spec.
import SeoArticle from "@/components/SeoArticle";

const ALL_LINKS = {
  "/park-home-repair-costs-uk":         { to: "/park-home-repair-costs-uk",         label: "Park Home Repair Costs UK" },
  "/park-home-roof-leak-advice":        { to: "/park-home-roof-leak-advice",        label: "Park Home Roof Leak Advice" },
  "/park-home-damp-problems":           { to: "/park-home-damp-problems",           label: "Park Home Damp Problems" },
  "/park-home-insurance-claims-advice": { to: "/park-home-insurance-claims-advice", label: "Park Home Insurance Claims Advice" },
  "/park-home-quote-too-high":          { to: "/park-home-quote-too-high",          label: "Is Your Quote Too High?" },
  "/park-home-urgent-repairs":          { to: "/park-home-urgent-repairs",          label: "Urgent Repairs — What To Do First" },
  "/quote-review":                      { to: "/quote-review",                      label: "Check Your Quote" },
  "/inspections":                       { to: "/inspections",                       label: "Independent Inspections" },
  "/insurance":                         { to: "/insurance",                         label: "Insurance Guidance" },
};

const rel = (...paths) => paths.map((p) => ALL_LINKS[p]).filter(Boolean);

// ---------------------------------------------------------------------------

export function ParkHomeRepairCostsUK() {
  return (
    <SeoArticle
      title="Park Home Repair Costs UK | What You Should Expect Before Paying"
      description="Understand real park home repair costs in the UK, what affects pricing, and how to avoid overpaying before committing to any work."
      keywords="park home repair costs UK, park home repair prices, park home quote check, park home renovation cost"
      canonical="https://parkhomesupport.co.uk/park-home-repair-costs-uk"
      heroImg="/images/inspections-hero.jpg"
      eyebrow="Repair costs explained"
      h1="Park Home Repair Costs UK — What You Should Expect Before Paying"
      intro="Understanding the cost of park home repairs is critical before committing to any work. Prices can vary significantly depending on the issue, the contractor, and how the scope is defined. Many park home owners end up overpaying simply because the work hasn't been properly assessed."
      sections={[
        {
          h2: "Typical Park Home Repair Costs",
          bullets: [
            "Roof repairs: £500 – £5,000+",
            "Damp and water ingress: £300 – £3,000+",
            "Substructure or base issues: £1,000 – £10,000+",
            "Cladding or external works: £800 – £6,000+",
          ],
        },
        {
          h2: "What Affects Cost",
          bullets: [
            "Extent of damage",
            "Accessibility",
            "Materials used",
            "Contractor pricing structure",
            "Whether the issue has been correctly diagnosed",
          ],
        },
        {
          h2: "Where People Overpay",
          body: "Many quotes include:",
          bullets: [
            "Unnecessary work",
            "Over-specified solutions",
            "Inflated labour or materials",
            "Lack of clear breakdown",
          ],
        },
        {
          h2: "Insurance Considerations",
          body: "Some damage may appear repair-related but could be linked to an insurable cause. Proceeding too quickly can result in paying for work that may otherwise have been covered.",
        },
        {
          h2: "Common Mistakes",
          bullets: [
            "Approving work without understanding the cause",
            "Not comparing scope properly",
            "Relying solely on contractor advice",
            "Missing potential insurance relevance",
          ],
        },
      ]}
      faq={[
        { q: "How much do park home repairs cost in the UK?",
          a: "Costs vary widely. Typical ranges: roof repairs £500–£5,000+, damp/water ingress £300–£3,000+, substructure £1,000–£10,000+, cladding £800–£6,000+. Many quotes include unnecessary or over-specified work, so an independent review before paying is recommended." },
        { q: "Why is my park home repair quote so expensive?",
          a: "Common reasons include over-scoped work, unnecessary items bundled in, inflated labour or materials, and a lack of clear breakdown. Quotes without itemisation are a common signal of inflation." },
        { q: "Can I challenge a repair quote after work has started?",
          a: "Once work starts, it becomes much harder to challenge the cost, scope or quality. Always have the quote reviewed before you commit." },
        { q: "Could my repair be covered by insurance?",
          a: "Possibly. Some damage appears repair-related but is actually linked to an insurable event. Proceeding before considering insurance can result in paying for work that could have been covered." },
      ]}
      related={rel("/park-home-urgent-repairs", "/park-home-roof-leak-advice", "/park-home-insurance-claims-advice")}
    />
  );
}

// ---------------------------------------------------------------------------

export function ParkHomeRoofLeakAdvice() {
  return (
    <SeoArticle
      title="Park Home Roof Leak | What To Do Before Paying For Repairs"
      description="Roof leaks in park homes can escalate quickly if not handled correctly. Understand the cause, the realistic cost and the insurance angle before approving any work."
      keywords="park home roof leak, park home roof repair, leaking park home, park home water ingress, park home roof advice"
      canonical="https://parkhomesupport.co.uk/park-home-roof-leak-advice"
      heroImg="/images/home-hero.jpg"
      eyebrow="Roof leaks"
      h1="Park Home Roof Leak — What To Do Before Paying For Repairs"
      intro="Roof leaks in park homes can quickly escalate if not handled correctly. The key is understanding the cause before approving repairs."
      sections={[
        {
          h2: "Common Causes",
          bullets: ["Failed seals", "Damaged roof coverings", "Poor installation", "Age-related wear"],
        },
        {
          h2: "Typical Repair Costs",
          bullets: [
            "Minor repairs: £200 – £800",
            "Mid-level repairs: £800 – £2,500",
            "Major roof works: £2,500 – £6,000+",
          ],
        },
        {
          h2: "When Repairs Go Wrong",
          body: "Many homeowners:",
          bullets: [
            "Replace more than necessary",
            "Misdiagnose the issue",
            "Overpay for urgent work",
          ],
        },
        {
          h2: "Insurance Angle",
          body: "Some roof damage may be linked to storm-related events. Identifying cause early is critical — once work starts, proving a sudden-event origin becomes much harder.",
        },
        {
          h2: "What To Do First",
          bullets: [
            "Identify source",
            "Gather evidence",
            "Review contractor quote carefully",
            "Consider independent review",
          ],
        },
      ]}
      faq={[
        { q: "How much does a park home roof leak repair cost?",
          a: "Minor repairs typically £200–£800, mid-level £800–£2,500, major works £2,500–£6,000+. Always confirm cause and scope before approving the higher-end quotes." },
        { q: "Is a park home roof leak covered by insurance?",
          a: "It can be — particularly for storm-related or impact damage with consequential damage. Document the cause and damage with dated photos BEFORE any remedial work begins." },
        { q: "Should I accept a full roof replacement for one leak?",
          a: "Almost never without a second opinion. Most leaks are caused by a localised fault (seal, felt, ridge, valley) — full replacement is proportionate only when the covering is end-of-life or damage is widespread." },
        { q: "What should I do first if my park home roof is leaking?",
          a: "Identify the source, gather photo evidence, request an itemised contractor quote, then have it independently reviewed before approving any work." },
      ]}
      related={rel("/park-home-urgent-repairs", "/park-home-repair-costs-uk", "/park-home-damp-problems")}
      decisionHeadline="Don't Approve That Work Yet"
      decisionBody="Get your quote checked before committing. Once work starts, leverage disappears."
      decisionCta={{ to: "/quote-review", label: "Upload Your Quote" }}
      primaryCta={{ to: "/quote-review", label: "Upload Your Quote" }}
    />
  );
}

// ---------------------------------------------------------------------------

export function ParkHomeDampProblems() {
  return (
    <SeoArticle
      title="Park Home Damp Problems | Causes, Costs & What To Do"
      description="Damp issues in park homes are common but often misunderstood. Treating the symptoms instead of the cause leads to wasted money. Independent UK guidance."
      keywords="park home damp, park home condensation, park home mould, park home damp cost, park home damp UK"
      canonical="https://parkhomesupport.co.uk/park-home-damp-problems"
      heroImg="/images/about-residential.jpg"
      eyebrow="Damp problems"
      h1="Park Home Damp Problems — What To Do Before You Pay"
      intro="Damp issues in park homes are common but often misunderstood. Treating the symptoms instead of the cause leads to wasted money."
      sections={[
        {
          h2: "Common Causes",
          bullets: ["Ventilation issues", "Water ingress", "Insulation defects", "Structural weaknesses"],
        },
        {
          h2: "Typical Costs",
          bullets: [
            "Minor treatment: £200 – £800",
            "Mid-level works: £800 – £2,500",
            "Structural damp fixes: £2,500+",
          ],
        },
        {
          h2: "Where Things Go Wrong",
          bullets: [
            "Treating visible damp only",
            "Ignoring underlying cause",
            "Over-specifying work",
          ],
        },
        {
          h2: "Insurance Considerations",
          body: "Most damp issues are not insured, but the originating cause might be. Establishing the cause properly can reveal a claimable event (e.g. storm, burst pipe, roof failure).",
        },
        {
          h2: "Best Approach",
          bullets: [
            "Identify cause first",
            "Avoid quick fixes",
            "Validate quote and scope",
          ],
        },
      ]}
      faq={[
        { q: "What causes damp in park homes most often?",
          a: "Ventilation issues, water ingress, insulation defects and structural weaknesses. Condensation is by far the most common category — and the one most frequently misdiagnosed as rising damp." },
        { q: "How much does it cost to fix damp in a park home?",
          a: "Minor treatment £200–£800, mid-level works £800–£2,500, structural fixes £2,500+. Establish the cause before agreeing to any invasive or structural work." },
        { q: "Can damp be claimed on my park home insurance?",
          a: "Most damp itself is excluded, but the originating event (e.g. storm, burst pipe, roof failure) may be claimable. Document the onset and source before work starts." },
        { q: "Should I accept a full strip-out quote for damp?",
          a: "Not without confirming the cause. Damp is the #1 category we see over-scoped. Always rule out condensation and ventilation before allowing invasive work." },
      ]}
      related={rel("/park-home-urgent-repairs", "/park-home-roof-leak-advice", "/park-home-insurance-claims-advice")}
      decisionHeadline="Avoid Paying for the Wrong Solution"
      decisionBody="Once work starts, it's harder to challenge. Get the diagnosis confirmed before you commit."
    />
  );
}

// ---------------------------------------------------------------------------

export function ParkHomeInsuranceClaimsAdvice() {
  return (
    <SeoArticle
      title="Park Home Insurance Claims Advice | What Is Covered & What Isn't"
      description="Understanding whether damage is insurable is one of the most important steps before approving repairs. Independent, non-regulated technical guidance for UK park home owners."
      keywords="park home insurance claims, park home insurance advice, park home damage claim, park home insurance guidance UK"
      canonical="https://parkhomesupport.co.uk/park-home-insurance-claims-advice"
      heroImg="/images/operators-band.jpg"
      eyebrow="Insurance claims guidance"
      h1="Park Home Insurance Claims — What Is Covered & What Isn't"
      intro="Understanding whether damage is insurable is one of the most important steps before approving repairs."
      sections={[
        {
          h2: "What Is Typically Covered",
          bullets: [
            "Sudden damage",
            "Storm-related issues",
            "Impact or accidental damage",
          ],
        },
        {
          h2: "What Is Usually Not Covered",
          bullets: [
            "Wear and tear",
            "Maintenance issues",
            "Gradual deterioration",
          ],
        },
        {
          h2: "Common Claim Mistakes",
          bullets: [
            "Claiming for non-covered issues",
            "Missing supporting evidence",
            "Proceeding with repairs too early",
          ],
        },
        {
          h2: "Uninsured Loss Risk",
          body: "Many homeowners unknowingly:",
          bullets: [
            "Pay for work that could have been claimed",
            "Submit claims that fail",
            "Miss key evidence early",
          ],
        },
        {
          h2: "Our Role",
          body: "We provide technical guidance based on real-world experience of how these issues are assessed. We are not a regulated insurance intermediary — we do not sell, arrange or manage insurance. We help you understand damage, repair scope and whether claim-related considerations apply so you're not submitting the wrong claim or walking away from a valid one.",
        },
      ]}
      faq={[
        { q: "What does park home insurance typically cover?",
          a: "Most park home policies cover sudden, accidental and specified-peril damage — typically storm damage, impact damage, escape of water, fire, theft and (with the extension) accidental damage. Policy wording varies." },
        { q: "What is usually NOT covered on a park home policy?",
          a: "Gradual wear and tear, poorly-maintained roofs or seals, general maintenance issues, and pre-existing damage present at policy inception are typically excluded." },
        { q: "Why do park home insurance claims fail?",
          a: "Common failures: wrong cause attribution (gradual wear submitted as storm), missing evidence (photos taken after work started), and scope mismatch (quote goes beyond the claimable event)." },
        { q: "Do you arrange park home insurance?",
          a: "No. We are not a regulated insurance intermediary. We provide independent, non-regulated technical guidance on damage, scope and claim-related considerations." },
      ]}
      related={rel("/park-home-repair-costs-uk", "/park-home-quote-too-high")}
      decisionHeadline="Avoid Uninsured Losses"
      decisionBody="Understand the situation before you act. Evidence gathered before work starts is worth thousands more than evidence after."
      decisionCta={{ to: "/insurance", label: "Request Guidance" }}
      primaryCta={{ to: "/insurance", label: "Request Guidance" }}
    />
  );
}

// ---------------------------------------------------------------------------

export function ParkHomeQuoteTooHigh() {
  return (
    <SeoArticle
      title="Park Home Repair Quote Too High | How To Check Before Paying"
      description="Many park home owners question whether a quote is reasonable — often after it's been approved. Getting it checked early can prevent thousands in unnecessary cost."
      keywords="park home quote too high, park home repair overpriced, park home contractor quote check, park home quote review"
      canonical="https://parkhomesupport.co.uk/park-home-quote-too-high"
      heroImg="/images/home-hero.jpg"
      eyebrow="Quote check"
      h1="Is Your Park Home Repair Quote Too High?"
      intro="Many park home owners question whether a quote is reasonable — often after it's already been approved. Getting it checked early can prevent unnecessary cost."
      sections={[
        {
          h2: "Signs a Quote May Be Too High",
          bullets: [
            "Lack of breakdown",
            "High labour rates",
            "Over-specified work",
            "Urgency pressure",
          ],
        },
        {
          h2: "What Contractors Don't Always Show",
          bullets: [
            "Alternative repair options",
            "Lower-cost approaches",
            "Scope flexibility",
          ],
        },
        {
          h2: "Typical Overpricing Issues",
          bullets: [
            "Inflated materials",
            "Duplicate work items",
            "Vague descriptions",
          ],
        },
        {
          h2: "What You Should Do",
          bullets: [
            "Request a breakdown",
            "Compare scope",
            "Get independent review",
          ],
        },
      ]}
      faq={[
        { q: "How do I know if a park home repair quote is too high?",
          a: "Red flags include a missing line-by-line breakdown, high labour rates without justification, over-specified scope (e.g. full replacement for a localised fault) and urgency pressure from the contractor." },
        { q: "Can I negotiate a park home repair quote?",
          a: "Yes. Request an itemised breakdown, get one or two comparison quotes, and have the scope independently reviewed. Most contractors expect scrutiny on quotes over £1,500." },
        { q: "What's the quickest way to check if my quote is fair?",
          a: "Submit it for an independent quote review. A specialist can identify over-scoping, duplicate items and scope inflation within 1–2 business days." },
        { q: "Should I proceed with the quote if I'm unsure?",
          a: "No. Once work starts, your leverage is gone. Check it properly first — every hour spent reviewing can save hundreds in unnecessary cost." },
      ]}
      related={rel("/park-home-repair-costs-uk", "/park-home-roof-leak-advice")}
      decisionHeadline="Don't Guess — Check It Properly"
      decisionBody="Once you approve, your leverage is gone."
    />
  );
}
